import { useState } from "react";
import { FaXmark } from "react-icons/fa6";
import { useTranslation } from "react-i18next";
import { css } from "../../../../styled-system/css";
import { Flex, Grid, VStack } from "../../../../styled-system/jsx";
import { Button } from "../../core/basic/Button";
import AssetSummary from "../../core/blocks/AssetSelector/AssetSummary";

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export type AssetSectionLabelSelector = (...args: any[]) => any;

export type AssetSection = {
  label: AssetSectionLabelSelector;
  assetIds: string[];
  assetType: "IMAGE" | "VIDEO";
  sectionKey?: string;
};

type Props = {
  sections: AssetSection[];
  title?: AssetSectionLabelSelector;
  onRemoveAsset?: (sectionKey: string, assetId: string) => void;
};

export const SelectedAssetsPreview = ({ sections, title, onRemoveAsset }: Props) => {
  const { t } = useTranslation(["advertising"]);
  const [isExpanded, setIsExpanded] = useState(false);
  const visibleSections = sections.filter((section) => section.assetIds.length);

  if (!visibleSections.length) {
    return null;
  }

  const resolvedTitle = title ? t(title) : t(($) => $.advertising["Selected Assets"]);

  return (
    <VStack
      gap="small"
      alignItems="start"
      border="1px solid"
      borderColor="gray.200"
      borderRadius="medium"
      width="100%"
      padding="medium"
    >
      <Flex justifyContent="space-between" alignItems="center" width="100%">
        <Button
          variant={"link"}
          onPress={() => {
            setIsExpanded((prev) => !prev);
          }}
        >
          {isExpanded ?
            t(($) => $.advertising["Hide {title}"], { title: resolvedTitle })
          : t(($) => $.advertising["Show {title}"], { title: resolvedTitle })}
        </Button>
      </Flex>
      {isExpanded ?
        <VStack gap="medium" width="100%">
          {visibleSections.map((section) => (
            <VStack
              key={section.assetType + ":" + (section.sectionKey ?? "")}
              gap="small"
              alignItems="start"
              width="100%"
            >
              <span
                className={css({
                  fontSize: "medium",
                  fontWeight: "medium",
                })}
              >
                {t(section.label)}
              </span>
              <Grid gap={"large"} gridTemplateColumns={"repeat(4, 1fr)"} paddingBottom={"large"}>
                {section.assetIds.map((assetId) => {
                  const sectionKey = section.sectionKey;
                  return (
                    <div className={css({ width: "100%" })} key={assetId}>
                      {onRemoveAsset && sectionKey ?
                        <Button
                          variant="contentOnly"
                          aria-label={t(($) => $.advertising["Remove asset"])}
                          className={css({
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            marginLeft: "auto",
                            color: "grey",
                            backgroundColor: "transparent",
                          })}
                          onPress={() => onRemoveAsset(sectionKey, assetId)}
                        >
                          <FaXmark size={12} />
                        </Button>
                      : null}
                      <AssetSummary
                        assetId={assetId}
                        assetType={section.assetType}
                        assetDescriptor={null}
                        hidden={false}
                        summaryContainerClasses={null}
                        displayAssetName={false}
                        columnSpan={12}
                      />
                    </div>
                  );
                })}
              </Grid>
            </VStack>
          ))}
        </VStack>
      : null}
    </VStack>
  );
};
